/**
 * Display helpers shared across the referrals area (list + detail).
 *
 * These live here rather than in either component because the list and the
 * per-user detail page render the SAME numbers and must make the same claim
 * about them. `fmtAvgOut` was previously copied into both files and the two
 * copies drifted: the list rounded a 0.4-minute average to `0m` ("walked in
 * and left immediately") while the detail page rendered `<1m`. One number,
 * two contradictory claims. One definition, imported twice, is the fix.
 */

/**
 * `minutes_out_avg` is null whenever every session was unclosed — including
 * when there were zero visits at all. That is "we don't know", never `0m`.
 *
 * A positive average under 30 seconds still rounds to `0` — render `<1m`
 * rather than `0m`, which would read as "they left immediately". A genuine
 * `0` average (only reachable from closed zero-length sessions) still renders
 * `0m`: that IS the claim in that case.
 */
export function fmtAvgOut(v: number | null): string {
  if (v === null) return '—';
  const rounded = Math.round(v);
  if (rounded === 0 && v > 0) return '<1m';
  return `${rounded.toLocaleString()}m`;
}

/**
 * A minute count rendered with thousands separators: `1,235m`. null renders
 * `—` — for a single visit that means the session never closed, which is NOT
 * a zero-minute visit, so it must never render `0m`.
 */
export function fmtMinutes(v: number | null): string {
  return v === null ? '—' : `${v.toLocaleString()}m`;
}
